import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { InputNumberComponent } from "@components/input-number/input-number.component";
import { DivListComponent } from "@components/list/div-list/div-list.component";
import { ListColumnDirective } from "@components/list/list-column.directive";
import { SelectorComponent } from "@components/selector/selector.component";
import _ from "lodash";
import { TradslagDTO } from "~app/common/models/tradslagsdata/TradslagDTO";
import { GuidUtils } from "~app/common/utils/guid-utils";
import { Atgard } from "~app/features/virkeskop/models/atgard-models";
import { VKAtgardRojningTradskikt, VKAtgardRojningTradslag } from "~app/features/virkeskop/models/skogsvard/skogsvard-models";

@Component({
    selector: "rask-skogsvard-tradskikt-tradslag",
    templateUrl: "./skogsvard-tradskikt-tradslag.component.html",
    styleUrl: "./skogsvard-tradskikt-tradslag.component.scss",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DivListComponent,
        ListColumnDirective,
        SelectorComponent,
        FormsModule,
        InputNumberComponent,
        ActionButtonComponent
    ]
})
export class SkogsvardTradskiktTradslagComponent implements OnInit {
    @Input() skogsvardsAtgard: Atgard;
    @Input() allaTradslag: TradslagDTO[];
    @Input() kontraktReadonly = false;
    @Input() rojningTradskikt: VKAtgardRojningTradskikt;
    @Output() updated = new EventEmitter<VKAtgardRojningTradskikt>();

    protected medelvardeHojd = 0;
    protected summaAntalStam = 0;

    ngOnInit(): void {
        this.summera();
    }

    protected viewFormatter = (td: TradslagDTO): string => td != null ? td.externkod : "";
    protected selectionFormatter = (item: TradslagDTO) => item?.id;

    protected tradslagChanged($event, tsGuid: string): void {
        if (!_.isNil($event)) {
            const rojningTradslag = this.rojningTradskikt?.tradslag?.find(x => x.guid === tsGuid);
            const sekvensNummer = this.allaTradslag.find(x => x.id === rojningTradslag.tradslagId)?.sekvensnummer;
            rojningTradslag.sekvensnummer = sekvensNummer;

            this.rojningTradskikt.tradslag = this.orderBy([...this.rojningTradskikt.tradslag]);
            this.update();
        }
    }

    protected somethingChanged($event): void {
        this.update();
    }

    protected excludedTradslag(id: number): TradslagDTO[] {
        const others = this.rojningTradskikt?.tradslag?.filter(x => x.tradslagId && x.tradslagId !== id).map(ts => ts.tradslagId);
        return this.allaTradslag?.filter(ts => others.some(o => o === ts.id));
    }

    protected addTradslag(): void {
        if (this.kontraktReadonly || this.skogsvardsAtgard.isReadonly) {
            return;
        }

        const newRojningTradslag = {
            tradslagId: null,
            guid: GuidUtils.newGuid(),
            tradhojd: 0,
            antalHa: 0,
            sekvensNummer: 0
        } as VKAtgardRojningTradslag;

        const tempTable = this.rojningTradskikt.tradslag.slice(0);
        tempTable.push(newRojningTradslag);
        this.rojningTradskikt.tradslag = tempTable;

        this.update();
    }

    protected deleteTradslag(tsGuid: string): void {
        const deletedTradslag = this.rojningTradskikt.tradslag.find(ts => ts.guid === tsGuid);
        const index = this.rojningTradskikt.tradslag.indexOf(deletedTradslag);
        this.rojningTradskikt.tradslag.splice(index, 1);

        const tempTable = this.rojningTradskikt.tradslag.slice(0);
        this.rojningTradskikt.tradslag = tempTable;

        this.update();
    }

    protected showSumRow(): boolean {
        return this.rojningTradskikt?.tradslag?.length > 0;
    }

    private orderBy(tradslag: VKAtgardRojningTradslag[]): VKAtgardRojningTradslag[] {
        return _.orderBy(tradslag, ["sekvensnummer"], ["asc"]);
    }

    private summera(): void {
        const rojningTradslag = this.rojningTradskikt?.tradslag;
        const tradslagWithHojd = this.rojningTradskikt?.tradslag.filter(ts => ts.tradhojd > 0);
        this.summaAntalStam = _.sumBy(rojningTradslag, t => t.antalHa);
        const localHojdSum = _.sumBy(rojningTradslag, t => t.tradhojd);
        const localMedelHojd = localHojdSum / tradslagWithHojd.length;
        if (!Number.isNaN(localMedelHojd)) {
            this.medelvardeHojd = _.round(localMedelHojd, 1);
        }
    }

    private update(): void {
        this.summera();
        this.updated.emit(this.rojningTradskikt);
    }
}
